import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Source: docs/use_case_video_series.md EP05 — "Warfarin & Dosing".

export const warfarinEleanor: Scenario = {
  slug: 'warfarin-eleanor',
  series: 'Drug Metabolism',
  title: "The dose kept changing. The target never settled.",
  socialHook:
    "Eleanor, 68: eight weeks on warfarin, and her INR kept swinging. The clue was simple: she cleared the drug slowly and was unusually sensitive to what remained.",
  meta: {
    description:
      "A fictional case study: Eleanor has atrial fibrillation and needs warfarin to prevent strokes, but her INR keeps swinging out of range. CYP2C9 and VKORC1 explain why the standard starting dose was too high for her.",
  },

  persona: {
    name: 'Eleanor',
    age: 68,
    sex: 'Female',
    ancestry: 'Northern European',
    occupation: 'Retired librarian',
    chiefConcern:
      "Eleanor has atrial fibrillation, an irregular heartbeat, and was started on warfarin eight weeks ago to prevent blood clots and strokes. Her INR — the test that shows whether the dose is in the safe range — keeps swinging too low or too high. She wants to know why her dose is so hard to settle.",
    familyHistory: 'Father: ischaemic stroke at age 72. No other known cardiac or bleeding conditions.',
  },

  clinicalPicture: {
    symptoms: [
      'Palpitations (occasional awareness of irregular heartbeat)',
      'Anxiety before weekly INR blood tests — not knowing which way the result will go',
      'Mild fatigue since starting warfarin',
    ],
    labs: [
      {
        name: 'INR (how "thin" her blood is)',
        value: 'Fluctuating — repeatedly outside the safe range',
        refRange: '2.0–3.0 (therapeutic target)',
        flag: 'high',
      },
      {
        name: 'Vitamin K dietary intake (estimated)',
        value: 'Low and inconsistent (variable leafy green intake)',
        refRange: 'Consistent intake recommended on warfarin',
        flag: 'low',
      },
    ],
    medications: ['Warfarin (dose being adjusted — currently trialling 3mg/day)', 'Bisoprolol 2.5mg daily (for heart rate control)'],
    supplements: ['No regular supplements'],
    lifestyle: [
      'Retired, moderately active — daily short walks',
      'Diet: home-cooked meals but variable vegetable intake week to week',
      'Non-smoker, occasional glass of wine',
      'No regular NSAIDs (anti-inflammatory painkillers) or aspirin',
    ],
  },

  genetics: [
    {
      gene: 'CYP2C9',
      variant: '*2 or *3',
      rsid: 'rs1799853 / rs1057910',
      genotype: 'Reduced function (intermediate or poor metabolizer)',
      plainMeaning:
        "Eleanor clears warfarin more slowly than average. CYP2C9 helps explain why a standard starting dose built up and hit harder than expected.",
      technicalDetail:
        "CYP2C9 is the liver enzyme responsible for metabolizing warfarin. A reduced-function variant (*2 or *3) slows clearance by 30–50%, causing the drug to accumulate with each dose. CPIC pharmacogenomics guidelines recommend a roughly 25–40% lower starting dose for carriers, with more frequent INR monitoring during initiation.",
    },
    {
      gene: 'VKORC1',
      variant: '-1639G>A',
      rsid: 'rs9923231',
      genotype: 'AA (homozygous — two copies of the A variant)',
      plainMeaning:
        "She is also more sensitive to the warfarin that stays in her system. VKORC1 helps explain why even a smaller amount can have a stronger effect.",
      technicalDetail:
        "VKORC1 encodes the protein warfarin directly inhibits — the vitamin K epoxide reductase complex. The AA genotype (homozygous variant) reduces VKORC1 expression, making the target more easily suppressed. Combined with her CYP2C9 variant, CPIC places this combination in the 'low dose required' category — typically under 3mg/day — compared to a standard 5mg starting dose.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "Every Monday brought a new number",
      body: "Eleanor's cardiologist started warfarin to lower stroke risk from atrial fibrillation. The goal was a steady INR in the safe window. Too low means blood may still clot too easily. Too high means bleeding risk rises. Week after week, her result landed on the wrong side. The dose went up, then down, then up again. Eleanor started dreading the blood test because the same pill never seemed to do the same thing twice.",
    },
    {
      type: 'narrative',
      heading: "INR is a narrow safety dial",
      body: "Think of INR as a dial for how long blood takes to clot. Warfarin pushes that number up on purpose, slowing clotting enough to prevent dangerous clots but not so much that bleeding becomes likely. Eleanor's readings have swung from too low — still at stroke risk — to too high — moving toward bleed risk. The mystery is why the standard dose had such an oversized and uneven effect.",
    },
    {
      type: 'reveal',
      heading: "The first clue was slow clearance",
      body: "Eleanor's body does not clear warfarin at the usual speed. Her CYP2C9 result means the liver enzyme that breaks down warfarin works more slowly, so the drug can stay longer and build with each dose. A standard starting dose can therefore act like a stronger dose in her body. Her INR did not rise because she did something wrong. It rose because the dose assumed faster clearance than she has.",
    },
    {
      type: 'reveal',
      heading: "The second clue was high sensitivity",
      body: "The warfarin that stays in Eleanor's system also has a stronger effect. Her VKORC1 AA genotype makes the drug target easier to suppress, so each milligram can do more than expected. Put the two clues together: the drug stays longer, and what stays hits harder. CPIC pharmacogenomics guidelines place this combination in a low-dose-required category, often under 3mg/day rather than a standard 5mg starting dose.",
    },
    {
      type: 'action',
      heading: "Four steps to steady the dose",
      items: [
        "Share the CYP2C9 and VKORC1 results with her cardiologist. Warfarin dosing is one of the best-validated areas of clinical pharmacogenomics.",
        "Ask about genotype-guided dose recalculation. CPIC guidelines at cpicpgx.org include dosing algorithms that combine gene results, age, and other factors.",
        "Keep vitamin K intake consistent, rather than avoiding it. Leafy greens such as spinach, kale, and broccoli can be part of her diet, but the amount should be steady week to week.",
        "Flag warfarin before any new medicine or supplement. Aspirin, NSAIDs such as ibuprofen, many antibiotics, and some herbal products can change bleeding risk or INR.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: "Why her dose need sits lower",
      caption:
        "This illustrates why Eleanor's INR kept drifting high. The number is not a risk score; it represents how far her likely dose need sits below the standard starting point.",
      baselineLabel: 'Standard warfarin starting dose (5mg/day)',
      baseline: 100,
      levers: [
        {
          label: 'CYP2C9 reduced function (*2/*3 variant)',
          delta: -30,
          description:
            "Her liver enzyme breaks warfarin down 30–50% more slowly than average, so a standard dose can build up.",
        },
        {
          label: 'VKORC1 AA genotype (high sensitivity)',
          delta: -20,
          description:
            "Her version of warfarin's target is more easily switched off, so each milligram can have a stronger effect.",
        },
        {
          label: 'Age 68 (clearance slows with age)',
          delta: -10,
          description:
            "Liver metabolism slows naturally with age. At 68, Eleanor may clear many drugs, including warfarin, more slowly than she would have at 40.",
        },
        {
          label: 'Low and variable vitamin K intake',
          delta: -8,
          description:
            "Vitamin K counteracts warfarin. When dietary intake is low or inconsistent, there is less natural buffering against the drug's effect — INR swings higher and more unpredictably.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'warfarin-eleanor',
    label: 'Explore your drug metabolism genetics in Coach',
  },
}
